import { useEffect } from 'react';

/**
 * Imperatively manage <head> for a page: title, description,
 * Open Graph, Twitter Card, canonical, and one or more JSON-LD blobs.
 *
 * No external library — react-helmet etc. would be a paid-shaped
 * dependency we can avoid for ~30 LOC of vanilla DOM.
 *
 * Usage:
 *   useDocumentMeta({
 *     title: 'Charizard VMAX',
 *     description: '…',
 *     image: 'https://…/charizard.jpg',
 *     url: '/product/123',
 *     type: 'product',
 *     jsonLd: [productSchema, breadcrumbSchema],
 *   });
 */
const MARK = 'data-meta-managed';

function setMeta(selector, attr, value) {
  if (value == null) return;
  let el = document.head.querySelector(`${selector}[${MARK}="1"]`);
  if (!el) {
    el = document.createElement(selector.split('[')[0]);
    el.setAttribute(MARK, '1');
    // Set the lookup attr right away so subsequent queries find it
    if (selector.includes('[')) {
      const m = selector.match(/\[(.+?)="(.+?)"\]/);
      if (m) el.setAttribute(m[1], m[2]);
    }
    document.head.appendChild(el);
  }
  if (attr === 'text') el.textContent = value;
  else el.setAttribute(attr, value);
}

function clearJsonLd() {
  document.head.querySelectorAll('script[type="application/ld+json"][data-jsonld="1"]').forEach(el => el.remove());
}

export default function useDocumentMeta({ title, description, image, url, type, jsonLd } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', 'content', description);
    if (description) setMeta('meta[property="og:description"]', 'content', description);
    if (title)       setMeta('meta[property="og:title"]', 'content', title);
    if (type)        setMeta('meta[property="og:type"]', 'content', type);
    if (url)         setMeta('meta[property="og:url"]', 'content', url);
    if (image)       setMeta('meta[property="og:image"]', 'content', image);
    if (image)       setMeta('meta[name="twitter:image"]', 'content', image);
    if (title)       setMeta('meta[name="twitter:title"]', 'content', title);
    if (description) setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    if (url) setMeta('link[rel="canonical"]', 'href', url);

    clearJsonLd();
    if (Array.isArray(jsonLd)) {
      for (const blob of jsonLd) {
        if (!blob) continue;
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-jsonld', '1');
        s.textContent = JSON.stringify(blob);
        document.head.appendChild(s);
      }
    }
  }, [title, description, image, url, type, JSON.stringify(jsonLd || null)]);
}
